import React, { useEffect, useState, ChangeEvent, FormEvent } from "react";
import { createPortal } from "react-dom";
import Objects from "../../constants/Objects";
import avaliations from "../../services/avaliations";
import { toast } from "react-toastify";
import { Avaliation } from "../../types";
import { ModalHeader, FormFooter, AIModal } from "./AvaliacaoFormComponents";
import { AIInputSection } from "./AIInputSection";
import {
  DadosGeraisSection,
  DobrasCutaneasSection,
  BioimpedanciaSection,
  FotosSection,
  InformacoesAdicionaisSection,
} from "./AvaliacaoFormSections";
import {
  getChangedFields,
  handleNestedFieldChange,
  applyFilteredFields,
} from "./AvaliacaoFormUtils";
import { useAnimatedPlaceholder } from "./useAnimatedPlaceholder";

interface AvaliacaoFormProps {
  isOpen: boolean;
  onBack: () => void;
  data: Partial<Avaliation> | null;
}

const AvaliacaoForm = ({ isOpen, onBack, data }: AvaliacaoFormProps) => {
  const [form, setForm] = useState<Avaliation>({
    ...Objects.avalicao,
    ...data,
  } as unknown as Avaliation);
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [collapsed, setCollapsed] = useState({
    dadosGerais: false,
    dobrasCutaneas: false,
    bioimpedancia: false,
    fotos: false,
    informacoesAdicionais: false,
  });
  const [isAIModalOpen, setIsAIModalOpen] = useState(false);
  const [aiDescription, setAIDescription] = useState("");
  const [aiLoading, setAILoading] = useState(false);
  const [aiResult, setAIResult] = useState<Partial<Avaliation> | null>(null);
  const [originalForm, setOriginalForm] = useState<Avaliation | null>(null);
  const [aiInputValue, setAIInputValue] = useState("");

  const animatedPlaceholder = useAnimatedPlaceholder({
    isOpen,
    isAIModalOpen,
    aiInputValue,
  });

  const toggleSection = (section: keyof typeof collapsed) => {
    setCollapsed((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(handleNestedFieldChange(form, name, value));
  };

  const handleSubmit = async (e?: FormEvent) => {
    e?.preventDefault();
    setLoading(true);

    // Usa form.id ou data?.id para determinar se é edição
    const avaliationId = form.id || form._id || data?.id || data?._id;

    if (avaliationId) {
      const resp = await avaliations.update(form);
      if (resp) {
        toast.success("Avaliação atualizada com sucesso!");
        onBack();
      } else {
        toast.error(
          "Erro ao atualizar a avaliação, por favor, tente novamente."
        );
      }
    } else {
      const resp = await avaliations.create(form);
      if (resp) {
        toast.success("Avaliação criada com sucesso!");
        onBack();
      } else {
        toast.error("Erro ao cadastrar avaliação, por favor, tente novamente.");
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    if (data) {
      setForm({ ...Objects.avalicao, ...data } as unknown as Avaliation);
    }
  }, [isOpen, data]);

  const handleAnalyzeDescription = async () => {
    if (!aiDescription.trim()) {
      toast.error("Por favor, insira uma descrição.");
      return;
    }

    setAILoading(true);
    try {
      const result = await avaliations.analyzeDescription(aiDescription.trim());

      if (result.error) {
        toast.error(result.message || "Erro ao processar descrição.");
        setAILoading(false);
        return;
      }

      setAIResult(result);
      // Loading será desligado pelo useEffect quando o modal estiver totalmente carregado
    } catch (error) {
      toast.error("Erro ao processar descrição.");
      setAILoading(false);
    }
  };

  // Auto-submit quando modal abre com descrição já preenchida
  useEffect(() => {
    if (isAIModalOpen && aiDescription.trim() && !aiResult && !aiLoading) {
      // Pequeno delay para garantir que o modal está totalmente renderizado
      const timeoutId = setTimeout(() => {
        handleAnalyzeDescription();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAIModalOpen]);

  // Desliga o loading quando o modal está aberto e tem resultados
  useEffect(() => {
    if (isAIModalOpen && aiResult && aiLoading) {
      // Pequeno delay para garantir que o modal está totalmente renderizado com os resultados
      const timeoutId = setTimeout(() => {
        setAILoading(false);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [isAIModalOpen, aiResult, aiLoading]);

  const handleApplyAIResult = (
    filteredFields: Record<string, { before: string; after: string }>
  ) => {
    if (!aiResult || Object.keys(filteredFields).length === 0) {
      toast.warning("Nenhum campo selecionado para aplicar.");
      return;
    }

    const baseForm = originalForm || form;
    const mergedData = applyFilteredFields(baseForm, filteredFields);

    setForm(mergedData as Avaliation);
    toast.success("Campos preenchidos com sucesso!");
    setIsAIModalOpen(false);
    setAIResult(null);
    setAIDescription("");
    setAIInputValue("");
    setOriginalForm(null);
  };

  const handleCancelAI = () => {
    setIsAIModalOpen(false);
    setAIResult(null);
    setAIDescription("");
    setOriginalForm(null);
  };

  const changedFields = getChangedFields(originalForm, aiResult);
  const hasChanges = Object.keys(changedFields).length > 0;

  const handleAIAnalyze = (text?: string) => {
    const description = text || aiInputValue.trim();
    if (description) {
      setOriginalForm({ ...form });
      setAIDescription(description);
      setIsAIModalOpen(true);
    }
  };

  const handleAudioAnalyze = async (audioBlob: Blob) => {
    setAILoading(true);
    try {
      const result = await avaliations.analyzeAudio(audioBlob);

      if (result.error) {
        toast.error(result.message || "Erro ao processar áudio.");
        setAILoading(false);
        return;
      }

      // Se tem transcrição, mostra no input
      if (result.transcription) {
        setAIInputValue(result.transcription);
      }

      // Se tem análise, processa como resultado de IA
      if (result.analysis) {
        setOriginalForm({ ...form });
        setAIResult(result.analysis);
        setIsAIModalOpen(true);
      } else if (result.transcription) {
        // Se só tem transcrição, usa como descrição
        setOriginalForm({ ...form });
        setAIDescription(result.transcription);
        setIsAIModalOpen(true);
      }
    } catch (error) {
      toast.error("Erro ao processar áudio.");
      setAILoading(false);
    }
  };

  const handleImageAnalyze = async (imageFile: File) => {
    setAILoading(true);
    try {
      const result = await avaliations.analyzeImage(imageFile);

      if (result.error) {
        toast.error(result.message || "Erro ao processar imagem.");
        setAILoading(false);
        return;
      }

      // Processa resultado como análise de IA
      setOriginalForm({ ...form });
      setAIResult(result);
      setIsAIModalOpen(true);
    } catch (error) {
      toast.error("Erro ao processar imagem.");
      setAILoading(false);
    }
  };

  if (!isOpen || !form) return null;

  return createPortal(
    <div
      className={`fixed inset-0 z-[99999999] flex items-center justify-center bg-black/60 transition-all duration-300 ${
        isExpanded ? "p-0" : "p-0 sm:p-4 sm:backdrop-blur-sm"
      }`}
    >
      <div
        className={`relative flex flex-col overflow-hidden bg-white shadow-2xl dark:bg-boxdark transition-all duration-300 ${
          isExpanded
            ? "max-h-screen h-full w-screen rounded-none"
            : "h-full w-full sm:h-[95vh] sm:max-w-5xl sm:rounded-2xl sm:border sm:border-stroke dark:sm:border-strokedark"
        }`}
      >
        <ModalHeader
          title="Avaliação Física"
          subtitle={data?.id ? "Editar avaliação existente" : "Nova avaliação"}
          isExpanded={isExpanded}
          onToggleExpand={() => setIsExpanded(!isExpanded)}
          onClose={onBack}
        />

        {/* Content */}
        <form
          onSubmit={handleSubmit}
          className="flex flex-1 flex-col overflow-hidden"
        >
          <div className="flex-1 space-y-4 sm:space-y-6 overflow-y-auto bg-gray-50/50 p-3 sm:p-6 md:p-8 dark:bg-gray-900/20 overscroll-contain">
            <AIInputSection
              value={aiInputValue}
              onChange={setAIInputValue}
              onAnalyze={handleAIAnalyze}
              onAudioAnalyze={handleAudioAnalyze}
              onImageAnalyze={handleImageAnalyze}
              placeholder={
                animatedPlaceholder || "Descreva a avaliação física..."
              }
              loading={aiLoading}
            />
<InformacoesAdicionaisSection
              form={form}
              onChange={handleChange}
              collapsed={collapsed}
              onToggleSection={toggleSection}
            />
            <DadosGeraisSection
              form={form}
              onChange={handleChange}
              onImagesChange={(images) =>
                setForm((prev) => ({ ...prev, images }))
              }
              collapsed={collapsed}
              onToggleSection={toggleSection}
            />

            <DobrasCutaneasSection
              form={form}
              onChange={handleChange}
              onImagesChange={(images) =>
                setForm((prev) => ({ ...prev, images }))
              }
              collapsed={collapsed}
              onToggleSection={toggleSection}
            />

            <BioimpedanciaSection
              form={form}
              onChange={handleChange}
              onImagesChange={(images) =>
                setForm((prev) => ({ ...prev, images }))
              }
              collapsed={collapsed}
              onToggleSection={toggleSection}
            />

            <FotosSection
              form={form}
              onChange={handleChange}
              onImagesChange={(images) =>
                setForm((prev) => ({ ...prev, images }))
              }
              collapsed={collapsed}
              onToggleSection={toggleSection}
            />

            
          </div>

          <FormFooter
            onCancel={onBack}
            onSubmit={handleSubmit}
            loading={loading}
          />
        </form>
      </div>

      <AIModal
        isOpen={isAIModalOpen}
        onClose={handleCancelAI}
        description={aiDescription}
        onDescriptionChange={setAIDescription}
        onAnalyze={handleAnalyzeDescription}
        onApply={handleApplyAIResult}
        onBack={() => {
          setAIResult(null);
          setAIDescription("");
        }}
        aiLoading={aiLoading}
        aiResult={aiResult}
        changedFields={changedFields}
        hasChanges={hasChanges}
      />
    </div>,
    document.body
  );
};

export default AvaliacaoForm;
